import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink, Facebook, Twitter, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { mockData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

const Footer = () => {
  const { currentLanguage } = useLanguage();
  
  // Traductions du footer
  const { translatedText: royalAssociationTranslated } = useTranslate("Association Royale");
  const { translatedText: descriptionTranslated } = useTranslate("Préservons l'héritage des familles fondatrices de Bruxelles depuis 1961, sous le haut patronage de Sa Majesté le Roi.");
  const { translatedText: quickLinksTranslated } = useTranslate("Liens rapides");
  const { translatedText: historyLignagesTranslated } = useTranslate("Histoire des Lignages");
  const { translatedText: publicationsTranslated } = useTranslate("Publications");
  const { translatedText: newsTranslated } = useTranslate("News");
  const { translatedText: eventsTranslated } = useTranslate("Événements");
  const { translatedText: associationTranslated } = useTranslate("L'Association");
  const { translatedText: sevenLignagesTranslated } = useTranslate("Les Sept Lignages");
  const { translatedText: contactTranslated } = useTranslate("Contact");
  const { translatedText: headquartersTranslated } = useTranslate("Siège social");
  const { translatedText: secretariatTranslated } = useTranslate("Secrétariat");
  const { translatedText: postalAddressTranslated } = useTranslate("Adresse postale");
  const { translatedText: copyrightTranslated } = useTranslate("© 2024 Association Royale des Descendants des Lignages de Bruxelles. Tous droits réservés.");
  const { translatedText: privacyPolicyTranslated } = useTranslate("Politique de confidentialité");
  const { translatedText: termsOfUseTranslated } = useTranslate("Conditions d'utilisation");
  const { translatedText: patronageTranslated } = useTranslate("Sous le haut patronage de Sa Majesté le Roi");

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid lg:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="lg:col-span-1 space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Logo Lignages de Bruxelles" 
                className="w-12 h-12 object-contain"
              />
              <div>
                <h3 
                  className="text-xl font-bold"
                  style={{ fontFamily: '"Lora", serif' }}
                >
                  Lignages de Bruxelles
                </h3>
                <p 
                  className="text-gray-400 text-sm"
                  style={{ fontFamily: '"Lora", serif' }}
                >
                  {royalAssociationTranslated}
                </p>
              </div>
            </div>
            <p 
              className="text-gray-300 leading-relaxed"
              style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
            >
              {descriptionTranslated}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-gray-800">
                <Facebook className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-gray-800">
                <Twitter className="w-5 h-5" />
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white hover:bg-gray-800">
                <Instagram className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Quick Links */}
          <div className="space-y-4">
            <h4 
              className="text-lg font-semibold"
              style={{ fontFamily: '"Lora", serif' }}
            >
              {quickLinksTranslated}
            </h4>
            <nav className="space-y-2">
              <Link 
                to="/lignages" 
                className="block text-gray-300 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {historyLignagesTranslated}
              </Link>
              <Link 
                to="/publications" 
                className="block text-gray-300 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {publicationsTranslated}
              </Link>
              <a 
                href="/#news" 
                className="block text-gray-300 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {newsTranslated}
              </a>
              <a 
                href="/#event" 
                className="block text-gray-300 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {eventsTranslated}
              </a>
              <Link 
                to="/association" 
                className="block text-gray-300 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              >
                {associationTranslated}
              </Link>
            </nav>
          </div>
          
          {/* Seven Lineages */}
          <div className="space-y-4">
            <h4 
              className="text-lg font-semibold"
              style={{ fontFamily: '"Lora", serif' }}
            >
              {sevenLignagesTranslated}
            </h4>
            <div className="space-y-2">
              {mockData.sevenLineages.map((lineage, index) => (
                <div 
                  key={index} 
                  className="text-gray-300"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  {lineage}
                </div>
              ))}
            </div>
          </div>
          
          {/* Contact Info */}
          <div className="space-y-4">
            <h4 
              className="text-lg font-semibold"
              style={{ fontFamily: '"Lora", serif' }}
            >
              {contactTranslated}
            </h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'rgb(196, 150, 96)' }} />
                <div 
                  className="text-gray-300"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  <strong>{headquartersTranslated}</strong><br/>
                  Galerie du Roi 5<br/>
                  1000 Bruxelles
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: 'rgb(196, 150, 96)' }} />
                <div 
                  className="text-gray-300"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  <strong>{secretariatTranslated}</strong><br/>
                  {postalAddressTranslated}:<br/>
                  Avenue Bois du Dimanche 3<br/>
                  1150 Bruxelles
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5" style={{ color: 'rgb(196, 150, 96)' }} />
                <a 
                  href="mailto:lignagesdebruxelles@gmail.com" 
                  className="text-gray-300 hover:text-white transition-colors"
                  style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                >
                  lignagesdebruxelles@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom Footer */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col space-y-1">
              <div 
                className="text-gray-400"
                style={{ fontFamily: '"Lora", serif', fontSize: '13px' }}
              >
                {copyrightTranslated}
              </div>
              <div 
                className="text-gray-500"
                style={{ fontFamily: '"Lora", serif', fontSize: '12px' }}
              >
                Site créé par © Renaud Ben Lakhal
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <Link 
                to="/privacy" 
                className="text-gray-400 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '13px' }}
              >
                {privacyPolicyTranslated}
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-400 hover:text-white transition-colors"
                style={{ fontFamily: '"Lora", serif', fontSize: '13px' }}
              >
                {termsOfUseTranslated}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;